import React, { useEffect, useState } from "react";
import {
  DynamicTable,
  DynamicTableHelper,
  TooltipWrapper,
  DateHelper,
} from "@workspace/common";
import { useSelector } from "react-redux";
import { Row, Col, Button } from "reactstrap";
import TableItemDisplay from "@workspace/common/src/Components/DynamicTable/TableItemDisplay";
import TableRowsPerPageWithNav from "@workspace/common/src/Components/DynamicTable/TableRowsPerPageWithNav";
import {
  UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS,
  UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS_FILTER,
  buildOptionsArray,
  generateUniversalReportFields,
  RECRUITER_DISTINCT_OPTIONS,
  RECRUITER_PARAMS,
} from "../UniversalReportConstant";
import { getJobReportStaticReportListing } from "../../../helpers/backend_helper";
import "./DynamicTableWrapper.scss";

function DynamicTableWrapper({
  data,
  pageInfo,
  pageRequest,
  pageRequestSet,
  config,
  search,
  setSearch,
  optGroup,
  setCustomConfigData,
  header,
  activeRow,
  setActiveRow,
  setTableConfig,
  jobStep,
  setIsReportOffcanvasOpen,
  isReportOffcanvasOpen,
  ...props
}) {
  // ================== Custom Render ==================
  // Calculate Ageing
  const calculateAgeing = (createdAt) => {
    const currentDate = new Date();
    const createdDate = new Date(createdAt);
    const ageingValueMS = currentDate - createdDate;
    const diffDays = Math.floor(ageingValueMS / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const customRenderList = [
    {
      names: ["updatedAt", "createdAt"],
      render: (data, opt) =>
        DateHelper.formatDateStandard2(
          DynamicTableHelper.getDynamicNestedResultForExport(data, opt?.name) ||
            ""
        ),
    },
    {
      names: ["job.jobSubmissionData.dateOpen"],
      render: (data) =>
        `${calculateAgeing(data?.job?.jobSubmissionData?.dateOpen)} Days`,
    },
  ];
  // ===================================================
  // Dispatch
  const jobUniversalReportListingMeta = useSelector(
    (state) => state?.ReportReducer?.jobUniversalReportListingMeta
  );

  const jobReportCustomViews = useSelector(
    (state) => state?.ReportReducer?.reportCustomViews
  );

  const defaultCustomView = useSelector(
    (state) => state?.ReportReducer?.defaultCustomView
  );

  const candidateForm = useSelector((state) => state.CandidateFormReducer.form);
  const jobForm = useSelector((state) => state.JobFormReducer.form);

  const handleClose = () => {
    setIsReportOffcanvasOpen(false);
  };

  useEffect(() => {
    if (!isReportOffcanvasOpen) {
      pageRequestSet.setPage(0);
      pageRequestSet.setPageSize(50);
    }
  }, [isReportOffcanvasOpen]);

  useEffect(() => {
    if (
      jobReportCustomViews != null &&
      jobReportCustomViews.length > 0 &&
      jobStep &&
      pageRequest?.jobStep !== jobStep
    ) {
      const selectedCustomView = jobReportCustomViews?.find(
        (customView) => customView?.selected
      );
      if (selectedCustomView) {
        const selectedGroup = selectedCustomView?.columnName?.split(",");
        const selectedObjects = selectedGroup?.map((value) => {
          // NOTE i use filter that allows for more column. It is different from
          // UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS
          return generateUniversalReportFields(candidateForm, jobForm)?.find(
            (option) => option?.value === value
          );
        });
        if (jobStep === RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT) {
          setCustomConfigData(RECRUITER_DISTINCT_OPTIONS);
        } else {
          if (selectedObjects.length > 0) {
            setCustomConfigData(selectedObjects);
          }
        }
        pageRequestSet.setFilterData(selectedCustomView?.filters);
        pageRequestSet.setExternalFilterData(
          selectedCustomView?.externalFilters
        );
      } else if (!selectedCustomView) {
        if (
          defaultCustomView?.columnName &&
          defaultCustomView?.columnName?.length > 0
        ) {
          const selectedObjects = defaultCustomView?.columnName?.map(
            (value) => {
              return generateUniversalReportFields(
                candidateForm,
                jobForm
              )?.find((option) => option?.value === value);
            }
          );
          if (jobStep === RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT) {
            setCustomConfigData(RECRUITER_DISTINCT_OPTIONS);
          } else {
            setCustomConfigData(selectedObjects);
          }
        } else {
          if (jobStep === RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT) {
            setCustomConfigData(RECRUITER_DISTINCT_OPTIONS);
          } else {
            setCustomConfigData(UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS);
          }
        }

        pageRequestSet.setFilterData(defaultCustomView?.filters ?? []);
        pageRequestSet.setExternalFilterData(
          defaultCustomView?.externalFilters ?? []
        );
      }
    }

    if (
      jobReportCustomViews != null &&
      jobReportCustomViews.length === 0 &&
      jobStep &&
      pageRequest?.jobStep !== jobStep
    ) {
      if (
        defaultCustomView?.columnName &&
        defaultCustomView?.columnName?.length > 0
      ) {
        const selectedObjects = defaultCustomView?.columnName?.map((value) => {
          return generateUniversalReportFields(candidateForm, jobForm)?.find(
            (option) => option?.value === value
          );
        });
        if (jobStep === RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT) {
          setCustomConfigData(RECRUITER_DISTINCT_OPTIONS);
        } else {
          setCustomConfigData(selectedObjects);
        }
      } else {
        if (jobStep === RECRUITER_PARAMS.ACTIVE_RECRUITERS_DISTINCT) {
          setCustomConfigData(RECRUITER_DISTINCT_OPTIONS);
        } else {
          setCustomConfigData(UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS);
        }
      }
      pageRequestSet.setFilterData(defaultCustomView?.filters ?? []);
      pageRequestSet.setExternalFilterData(
        defaultCustomView?.externalFilters ?? []
      );
    }
  }, [jobReportCustomViews, jobStep]);

  const enableDefaultView = () => {
    setCustomConfigData(UNIVERSAL_REPORT_STATIC_INITIAL_OPTIONS);
    pageRequestSet.setFilterData([]);
  };

  // Export Excel
  const handleEportExcel = async () => {
    let exportData = null;
    try {
      const payload = {
        ...pageRequest,
        jobStep,
        isDownload: true,
        filters: props?.bubbleExternalFilter,
      };
      const resp = await getJobReportStaticReportListing(payload);
      exportData = resp?.data?.jobs;
      if (activeRow?.length > 0) {
        exportData = exportData.filter((item) => activeRow.includes(item?.id));
      }
    } catch (e) {}

    DynamicTableHelper.handleExportExcel(
      `Job Timeline Report - (${jobStep})`,
      exportData,
      config.slice(2),
      customRenderList,
      true
    );
  };

  return (
    <div>
      <Row style={{ borderBottom: "1px solid lightgray" }}>
        <Col lg={12}>
          <div className="d-flex flex-row justify-content-between align-items-center mb-3">
            <div className="d-flex flex-row gap-3 align-items-center justify-content-center ">
              <div className="fw-semibold fs-5">
                {jobStep}
                {jobUniversalReportListingMeta?.isLoading
                  ? " (0)"
                  : ` (${pageInfo?.totalElements || 0})`}
              </div>
              <div className="search-box">
                {setSearch && (
                  <form onSubmit={pageRequestSet.setSearchTerm}>
                    <input
                      type="text"
                      placeholder="Search.."
                      className="form-control search border-light"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <span className="mdi mdi-magnify search-icon"></span>
                  </form>
                )}
              </div>
            </div>
            <div className="d-flex flex-row gap-3 align-items-center justify-content-center ">
              <TableItemDisplay
                pageInfo={pageInfo}
                isLoading={jobUniversalReportListingMeta?.isLoading}
              />
              <div
                style={{
                  width: "2px",
                  height: "20px",
                  backgroundColor: "#adb5bd",
                  marginLeft: "12px",
                }}
              ></div>
              <TableRowsPerPageWithNav
                pageInfo={pageInfo}
                pageRequestSet={pageRequestSet}
              />
              <TooltipWrapper tooltipText="Export to Excel">
                <Button
                  color="light"
                  className="report-listing-filter-btn  fw-bold d-flex flex-row align-items-center px-1 justify-content-center"
                  onClick={handleEportExcel}
                  style={{
                    height: "25px",
                    width: "28px",
                  }}
                >
                  <i
                    className="ri-download-fill align-bottom"
                    style={{
                      fontSize: "12px",
                    }}
                  ></i>
                </Button>
              </TooltipWrapper>
              <span
                className="mdi mdi-close cursor-pointer"
                onClick={handleClose}
              ></span>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12} className="report-oc-wrapper">
          {jobStep !== "" ? (
            <DynamicTable
              config={config}
              data={data}
              pageRequestSet={pageRequestSet}
              pageInfo={pageInfo}
              isLoading={jobUniversalReportListingMeta?.isLoading ?? true}
              freezeHeader={true}
              activeRow={activeRow}
              setTableConfig={setTableConfig}
              pageRequest={pageRequest}
              tableHeight="100%"
            />
          ) : (
            <div className="mt-3">
              <span className="form-text">
                Data is currently not available.
              </span>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default DynamicTableWrapper;
